import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import SearchableView from "./SearchableView";
import Popup from "./Popup";
import PageNavigator from "../classes/PageNavigator";
import PaymentStatus from "../classes/PaymentStatus";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import AddOrEditInvoiceView from "./AddOrEditInvoiceView";
import MessageDispatch from "../rocket/MessageDispatch";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import Administrator from "../models/Administrator";
import Invoice from "../models/Invoice";
import Duration from "../core/Duration";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import InvoicesRequest from "../models/InvoicesRequest";
import HospitalsRequest from "../models/HospitalsRequest";
import Timer from "../utils/Timer";
import NOKUser from "../models/NOKUser";
import DateFormat from "../classes/DateFormat";
import SearchRequestsRequest from "../models/SearchRequestsRequest";
import Notification from "../models/Notification";
import DateTime from "../core/DateTime";
import SearchRequest from "../models/SearchRequest";
import Hospital from "../models/Hospital";
import Invoices from "../classes/Invoices";
import LabelDropdown from "./LabelDropdown";
import { UsageConstants } from "../rocket/D3ETemplate";
import { StyleThemeData } from "./ThemeWrapper";
import { BuildContext } from "../classes/BuildContext";

type _AddButtonOnPressed = (d3eState: SystemAdminInvoiceListRefs) => void;

type _LabelDropdown2OnChanged = (
  text: string,
  d3eState: SystemAdminInvoiceListRefs
) => void;

export interface SystemAdminInvoiceListProps extends BaseUIProps {
  key?: string;
  admin: Administrator;
  user: NOKUser;
  notification?: Notification;
}
/// To store state data for SystemAdminInvoiceList
class SystemAdminInvoiceListRefs {
  public addButton: AddButtonState = new AddButtonState();
}

interface AddButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SystemAdminInvoiceListRefs;
  _onAddButtonClicked?: _AddButtonOnPressed;
}

class AddButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddButtonWithState extends ObservableComponent<AddButtonWithStateProps> {
  addButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addButton(): AddButtonState {
    return this.props.d3eState.addButton;
  }
  public get d3eState(): SystemAdminInvoiceListRefs {
    return this.props.d3eState;
  }
  public get _onAddButtonClicked(): _AddButtonOnPressed {
    return this.props._onAddButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("addButton", null, this.addButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["addButton", "addButton.", "addButton.hover"], this.rebuild);
  }
  public addButtonOnEnter(event): void {
    return this.addButton.setHover(true);
  }
  public addButtonOnExit(event): void {
    return this.addButton.setHover(false);
  }
  public dispose(): void {
    this.addButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 5.0, new Map()),
      child: Button({
        padding: this.addButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.addButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.addButton.disable,
        onPressed: () => {
          this._onAddButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        states: ui.joinStates({ "data-visibility": false }, {}),
        child: TextView({ data: "+ Add Invoice" }),
        onEnter: (event) => {
          this.addButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addButtonOnExit(event);
        },
      }),
      className: "x692c",
      states: ui.joinStates({ "data-visibility": false }, {}),
    });
  }
}
function AddButtonWithState(props: AddButtonWithStateProps) {
  return React.createElement(_AddButtonWithState, props);
}

class _SystemAdminInvoiceListState extends ObservableComponent<SystemAdminInvoiceListProps> {
  static defaultProps = { admin: null, user: null, notification: null };
  d3eState: SystemAdminInvoiceListRefs = new SystemAdminInvoiceListRefs();
  query: Invoices = null;
  request: InvoicesRequest = null;
  invoicesList: Array<Invoice> = ListWrapper.widget(this, "invoicesList");
  invoice: Invoice = null;
  hospital: Hospital = null;
  services: Array<SearchRequest> = ListWrapper.widget(this, "services");
  hospitals: Array<Hospital> = ListWrapper.widget(this, "hospitals");
  hosipitalRequest: HospitalsRequest = null;
  isInvisibileNotification: boolean = true;
  monthList: Array<string> = ListWrapper.widget(this, "monthList");
  filteredMonth: string = "";
  addInvoicePopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SystemAdminInvoiceListProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get notification(): Notification {
    return this.props.notification;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "request",
        "request.applyMonth",
        "request.applyStatus",
        "request.ascending",
        "request.hospital",
        "request.monthNumber",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.status",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeInvoicesList);

    this.computeInvoicesList();

    this.computeServices();

    this.on(
      [
        "hosipitalRequest",
        "hosipitalRequest.applyStatus",
        "hosipitalRequest.ascending",
        "hosipitalRequest.hospital",
        "hosipitalRequest.offset",
        "hosipitalRequest.orderBy",
        "hosipitalRequest.pageSize",
        "hosipitalRequest.status",
      ],
      this.computeHospitals
    );

    this.computeHospitals();

    this.updateSyncProperty("notification", this.props.notification);

    this.computeMonthList();

    this.on(
      [
        "admin",
        "filteredMonth",
        "hospital",
        "hospitals",
        "invoicesList",
        "invoicesList.amount",
        "invoicesList.dueDate",
        "invoicesList.hospital",
        "invoicesList.hospital.name",
        "invoicesList.invoiceDate",
        "invoicesList.requestCount",
        "invoicesList.status",
        "isInvisibileNotification",
        "monthList",
        "notification",
        "notification.createdAt",
        "query",
        "query.count",
        "query.items",
        "request",
        "request.offset",
        "request.pageSize",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: SystemAdminInvoiceListProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }

    if (prevProps.notification !== this.props.notification) {
      this.updateObservable(
        "notification",
        prevProps.notification,
        this.props.notification
      );

      this.fire("notification", this);
    }
  }
  public setQuery(val: Invoices): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getInvoices(
          UsageConstants.QUERY_GETINVOICES_SYSTEMADMININVOICELIST_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setRequest(val: InvoicesRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setInvoicesList(val: Array<Invoice>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.invoicesList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("invoicesList", this.invoicesList, val);

    this.invoicesList.clear();

    this.invoicesList.addAll(val);

    this.fire("invoicesList", this);
  }
  public addToInvoicesList(val: Invoice, index: number = -1): void {
    if (index === -1) {
      if (!this.invoicesList.contains(val)) this.invoicesList.add(val);
    } else {
      this.invoicesList.remove(this.invoicesList.elementAt(index));

      this.invoicesList.add(val);
    }

    this.fire("invoicesList", this, val, true);

    this.updateObservable("invoicesList", null, val);
  }
  public removeFromInvoicesList(val: Invoice): void {
    this.invoicesList.remove(val);

    this.fire("invoicesList", this, val, false);

    this.removeObservable("invoicesList", val);
  }
  public computeInvoicesList = (): void => {
    try {
      this.setInvoicesList(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(
        " exception in computeInvoicesList : " + exception.toString()
      );

      this.setInvoicesList([]);
    }
  };
  public setInvoice(val: Invoice): void {
    let isValChanged: boolean = this.invoice !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("invoice", this.invoice, val);

    this.invoice = val;

    this.fire("invoice", this);
  }
  public setHospital(val: Hospital): void {
    let isValChanged: boolean = this.hospital !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("hospital", this.hospital, val);

    this.hospital = val;

    this.fire("hospital", this);
  }
  public setServices(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.services, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("services", this.services, val);

    this.services.clear();

    this.services.addAll(val);

    this.fire("services", this);
  }
  public addToServices(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.services.contains(val)) this.services.add(val);
    } else {
      this.services.remove(this.services.elementAt(index));

      this.services.add(val);
    }

    this.fire("services", this, val, true);

    this.updateObservable("services", null, val);
  }
  public removeFromServices(val: SearchRequest): void {
    this.services.remove(val);

    this.fire("services", this, val, false);

    this.removeObservable("services", val);
  }
  public computeServices = async (): Promise<void> => {
    try {
      this.setServices(
        Array.from(
          (
            await Query.get().getSearchRequests(
              UsageConstants.QUERY_GETSEARCHREQUESTS_SYSTEMADMININVOICELIST_PROPERTIES_SERVICES_COMPUTATION,
              new SearchRequestsRequest({
                pageSize: 1000,
                offset: 0,
                orderBy: "",
                ascending: true,
              })
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeServices : " + exception.toString());

      this.setServices([]);
    }
  };
  public setHospitals(val: Array<Hospital>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.hospitals,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("hospitals", this.hospitals, val);

    this.hospitals.clear();

    this.hospitals.addAll(val);

    this.fire("hospitals", this);
  }
  public addToHospitals(val: Hospital, index: number = -1): void {
    if (index === -1) {
      if (!this.hospitals.contains(val)) this.hospitals.add(val);
    } else {
      this.hospitals.remove(this.hospitals.elementAt(index));

      this.hospitals.add(val);
    }

    this.fire("hospitals", this, val, true);

    this.updateObservable("hospitals", null, val);
  }
  public removeFromHospitals(val: Hospital): void {
    this.hospitals.remove(val);

    this.fire("hospitals", this, val, false);

    this.removeObservable("hospitals", val);
  }
  public computeHospitals = async (): Promise<void> => {
    try {
      this.setHospitals(
        Array.from(
          (
            await Query.get().getHospitals(
              UsageConstants.QUERY_GETHOSPITALS_SYSTEMADMININVOICELIST_PROPERTIES_HOSPITALS_COMPUTATION,
              this.hosipitalRequest
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeHospitals : " + exception.toString());

      this.setHospitals([]);
    }
  };
  public setHosipitalRequest(val: HospitalsRequest): void {
    let isValChanged: boolean = this.hosipitalRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("hosipitalRequest", this.hosipitalRequest, val);

    this.hosipitalRequest = val;

    this.fire("hosipitalRequest", this);
  }
  public setIsInvisibileNotification(val: boolean): void {
    let isValChanged: boolean = this.isInvisibileNotification !== val;

    if (!isValChanged) {
      return;
    }

    this.isInvisibileNotification = val;

    this.fire("isInvisibileNotification", this);
  }
  public setMonthList(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.monthList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.monthList.clear();

    this.monthList.addAll(val);

    this.fire("monthList", this);
  }
  public addToMonthList(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.monthList.contains(val)) this.monthList.add(val);
    } else {
      this.monthList.remove(this.monthList.elementAt(index));

      this.monthList.add(val);
    }

    this.fire("monthList", this, val, true);
  }
  public removeFromMonthList(val: string): void {
    this.monthList.remove(val);

    this.fire("monthList", this, val, false);
  }
  public computeMonthList = (): void => {
    try {
      this.setMonthList(
        Array.from([
          "All",
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ])
      );
    } catch (exception) {
      console.log(" exception in computeMonthList : " + exception.toString());

      this.setMonthList([]);
    }
  };
  public setFilteredMonth(val: string): void {
    let isValChanged: boolean = this.filteredMonth !== val;

    if (!isValChanged) {
      return;
    }

    this.filteredMonth = val;

    this.fire("filteredMonth", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            ui.Row({
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.symmetric({
                    horizontal: 0.0,
                    vertical: 10.0,
                    transitions: new Map(),
                  }),
                  width: 300,
                  child: SearchableView<Hospital>({
                    placeHolder: "Search",
                    value: this.hospital,
                    items: this.hospitals,
                    name: "Hospital",
                    onChanged: (item) => {
                      this.onChangedSearchComponentHandler(item, this.d3eState);
                    },
                    states: ui.joinStates(
                      { "data-visibility": this.admin !== null },
                      {}
                    ),
                  }),
                  key: "0",
                  className: "x238 hc",
                  states: ui.joinStates(
                    { "data-visibility": this.admin !== null },
                    {}
                  ),
                }),
                ui.Container({
                  margin: ui.EdgeInsets.fromLTRB(0.0, 5.0, 0.0, 0.0, new Map()),
                  child: LabelDropdown<string>({
                    placeHolder: "---Select---",
                    name: "Select Month",
                    value: this.filteredMonth,
                    items: this.monthList,
                    width: 250,
                    onChanged: (text) => {
                      this.labelDropdown2onChanged(text, this.d3eState);

                      this.onSelectMonthHandler(text, this.d3eState);
                    },
                    builder: (context, item) => {
                      return TextView({ data: item });
                    },
                  }),
                  key: "1",
                  className: "x98bd",
                }),
              ],
              key: "0",
            }),
            this.admin !== null
              ? ui.Row({
                  children: [
                    AddButtonWithState({
                      d3eState: this.d3eState,
                      _onAddButtonClicked: this.onAddButtonClicked,
                      key: "0",
                    }),
                  ],
                  className: "xc28",
                })
              : [],
          ],
          className: "x5a6 hc h",
          key: "0",
        }),
        ui.Column({
          children: [
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  columnWidths: {
                    0: new ui.FlexColumnWidth(1),
                    1: new ui.FlexColumnWidth(1),
                    2: new ui.FlexColumnWidth(1),
                    3: new ui.FlexColumnWidth(1),
                    4: new ui.FlexColumnWidth(1),
                    5: new ui.FixedColumnWidth(150),
                  },
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          child: TextView({
                            data: "Hospital",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Invoice Date",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Due Date",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Number of Requests",
                            textAlign: ui.TextAlign.center,
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "3",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Amount",
                            textAlign: ui.TextAlign.center,
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "4",
                        }),
                        ui.TableCell({
                          child: TextView({
                            data: "Payment Status",
                            textAlign: ui.TextAlign.center,
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "5",
                        }),
                      ],
                      className: "x643 hc",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isNotEmpty
                      ? this.invoicesList.expand((item) => [
                          ui.TableRow({
                            states: ui.joinStates(
                              {
                                "data-c0":
                                  this.notification !== null &&
                                  item.invoiceDate ===
                                    this.notification.createdAt &&
                                  this.isInvisibileNotification,
                              },
                              {}
                            ),
                            decoration:
                              this.notification !== null &&
                              item.invoiceDate ===
                                this.notification.createdAt &&
                              this.isInvisibileNotification
                                ? new ui.BoxDecoration({
                                    color: new ui.Color(0xffe9ecf2),
                                  })
                                : new ui.BoxDecoration({}),
                            children: [
                              ui.TableCell({
                                child: TextView({
                                  data: item?.hospital?.name ?? "",
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "0",
                              }),
                              ui.TableCell({
                                child: TextView({
                                  data:
                                    new DateFormat("dd/MM/yyyy").format(
                                      item?.invoiceDate ?? DateTime.now()
                                    ) ?? "",
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "1",
                              }),
                              ui.TableCell({
                                child: TextView({
                                  data:
                                    new DateFormat("dd/MM/yyyy").format(
                                      item?.dueDate ?? DateTime.now()
                                    ) ?? "",
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "2",
                              }),
                              ui.TableCell({
                                child: TextView({
                                  data: item?.requestCount?.toString() ?? "",
                                  textAlign: ui.TextAlign.center,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "3",
                              }),
                              ui.TableCell({
                                child: TextView({
                                  data: item?.amount?.toString() ?? "",
                                  textAlign: ui.TextAlign.center,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "4",
                              }),
                              ui.TableCell({
                                child: TextView({
                                  data: item.status.name,
                                  textAlign: ui.TextAlign.center,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  states: ui.joinStates(
                                    {
                                      "data-c0":
                                        item.status === PaymentStatus.Paid,
                                      "data-c1":
                                        item.status === PaymentStatus.Unpaid,
                                      "data-c2":
                                        item.status === PaymentStatus.Fail,
                                    },
                                    {}
                                  ),
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color:
                                      item.status === PaymentStatus.Paid
                                        ? cStyle.c24
                                        : item.status === PaymentStatus.Unpaid
                                        ? cStyle.c8
                                        : item.status === PaymentStatus.Fail
                                        ? cStyle.c25
                                        : cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "xcc hc",
                                }),
                                className: "hc",
                                key: "5",
                              }),
                            ],
                            onTap: (e) => {
                              e.stopPropagation();

                              this.onEditInvoiceHandler(item, this.d3eState);
                            },
                            className: "x7ed hc",
                            key: item?.ident,
                          }),
                        ])
                      : [],
                  ],
                  className: "x3d hc h",
                  key: "0",
                }),
                this.query !== null && this.query.items.isEmpty
                  ? ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        ui.AssetImage({
                          path: "images/emptyList.png",
                          width: 300,
                          height: 300,
                          className: "xa41 hc vc",
                          key: "0",
                        }),
                        TextView({
                          data: "No Records",
                          style: new ui.TextStyle({
                            fontWeight: ui.FontWeight.bold,
                            fontSize: 16,
                          }),
                          className: "x40e hc",
                          key: "1",
                        }),
                      ],
                      className: "xf7b hc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  children: [
                    PaginationView({
                      pageSize: this.request.pageSize,
                      total: this.query !== null ? this.query.count : 0,
                      offset: this.request.offset,
                      onChange: (pageSize, offset) => {
                        this.onPaginationViewChanged(
                          pageSize,
                          offset,
                          this.d3eState
                        );
                      },
                      states: ui.joinStates(
                        {
                          "data-visibility":
                            this.query !== null && this.query.items.isNotEmpty,
                        },
                        {}
                      ),
                      key: "0",
                    }),
                  ],
                  className: "xac2f hc h",
                  key: "2",
                }),
              ],
              className: "x827 hc h",
              key: "0",
            }),
          ],
          className: "x03a hc h",
          key: "1",
        }),
      ],
      className: ui.join(
        this.props.className,
        "SystemAdminInvoiceList xcb hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    new Timer(new Duration({ seconds: 5 }), () => {
      this.setIsInvisibileNotification(false);
    });

    if (this.admin !== null) {
      this.setRequest(
        new InvoicesRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          applyStatus: false,
        })
      );

      this.setHosipitalRequest(
        new HospitalsRequest({
          pageSize: 1000,
          offset: 0,
          orderBy: "",
          ascending: true,
        })
      );
    } else if (this.user !== null) {
      this.setRequest(
        new InvoicesRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          hospital: this.user.hospital,
          status: null,
          applyStatus: false,
        })
      );
    }
  };
  public onPaginationViewChanged = (
    pageSize: number,
    offset: number,
    d3eState: SystemAdminInvoiceListRefs
  ): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onAddButtonClicked = (d3eState: SystemAdminInvoiceListRefs): void => {
    this.setInvoice(new Invoice({ title: "Invoice" }));

    this.showAddInvoice();
  };
  public onChangedSearchComponentHandler = (
    item: Hospital,
    d3eState: SystemAdminInvoiceListRefs
  ): void => {
    this.setHospital(item);

    this.setRequest(
      new InvoicesRequest({
        pageSize: 50,
        offset: 0,
        orderBy: "sortOrder",
        ascending: true,
        hospital: this.hospital,
        status: null,
        applyStatus: false,
      })
    );

    this.setRequest(this.request.deepClone());
  };
  public onEditInvoiceHandler = (
    item: Invoice,
    d3eState: SystemAdminInvoiceListRefs
  ): void => {
    this.setInvoice(item);

    this.navigator.pushBillingDetailsView({
      admin: this.admin,
      user: this.user,
      obj: this.invoice,
      target: "main",
      replace: false,
    });
  };
  public onSelectMonthHandler = (
    value: string,
    d3eState: SystemAdminInvoiceListRefs
  ): void => {
    this.setFilteredMonth(value);

    if (this.filteredMonth === "All") {
      this.setRequest(
        new InvoicesRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          hospital: this.hospital,
          status: null,
          applyStatus: false,
          applyMonth: false,
        })
      );
    } else {
      let monthNumber: number = this.monthList.indexOf(value);

      this.setRequest(
        new InvoicesRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "sortOrder",
          ascending: true,
          hospital: this.hospital,
          status: null,
          applyStatus: false,
          applyMonth: true,
          monthNumber: monthNumber,
        })
      );
    }

    this.setRequest(this.request.deepClone());
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    this.addInvoicePopup?.dispose();

    super.dispose();
  }
  public showAddInvoice(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.addInvoicePopup?.dispose();

    /*
TODO ThemeWrapper.of(this.context);
*/

    let cStyle = StyleThemeData.current;

    this.addInvoicePopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 850,
        child: AddOrEditInvoiceView({
          obj: this.invoice,
          requests: this.services,
        }),
        className: "xb01 hc vc",
      }),
    });

    this.addInvoicePopup.showPopup(this.context);
  }
  public hideAddInvoice(): void {
    this.addInvoicePopup?.dispose();
  }
  public labelDropdown2onChanged = (
    val: string,
    d3eState: SystemAdminInvoiceListRefs
  ): void => {
    this.setFilteredMonth(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get addButton() {
    return this.d3eState.addButton;
  }
}
export default function SystemAdminInvoiceList(
  props: SystemAdminInvoiceListProps
) {
  return React.createElement(_SystemAdminInvoiceListState, {
    ..._SystemAdminInvoiceListState.defaultProps,
    ...props,
  });
}
